import { useMemo } from "react";
import { useSelector } from "react-redux";
import {
  AlgoliaFacetAttribute,
  MasterAuthStatusValue,
  SideNavElementId,
} from "../../../enums/worklistEnums";
import { RootState } from "../../../redux/store/store";
import { useQuickReviewOrdersCount } from "./useQuickReviewOrdersCount";
import { useReviewPendingCount } from "./useReviewPendingCount";

/**
 * Custom hook that computes the order counts for each side navigation element.
 *
 * This hook fetches Algolia facets data from Redux store, review-pending
 * counts, and the Quick Review board count, then maps them to their
 * corresponding side navigation element IDs.
 *
 * @returns {Object} An object containing:
 * @returns {Record<string, number>} counts - A mapping of SideNavElementId to order count.
 * @returns {boolean} loading - Whether the facets count data is still loading.
 */
export const useSideNavOrderCounts = () => {
  const {
    countsBySidebarId: reviewPendingCounts,
    loading: reviewPendingLoading,
  } = useReviewPendingCount();
  const { data: quickReviewCountState, loading: quickReviewLoading } =
    useQuickReviewOrdersCount();
  const { loading: postgresLoading, data: postgresFacetsCount } = useSelector(
    (state: RootState) => state.postgresFacetCounts,
  );

  const counts = useMemo(() => {
    const masterAuthStatus =
      postgresFacetsCount?.facet_results?.[
        AlgoliaFacetAttribute.MasterAuthStatus
      ];

    return {
      [SideNavElementId.MyPaOrders]:
        masterAuthStatus?.[MasterAuthStatusValue.New] ?? 0,
      [SideNavElementId.AuthRequired]:
        masterAuthStatus?.[MasterAuthStatusValue.AuthRequired] ?? 0,
      [SideNavElementId.Pending]:
        masterAuthStatus?.[MasterAuthStatusValue.Pending] ?? 0,
      [SideNavElementId.Query]:
        masterAuthStatus?.[MasterAuthStatusValue.Query] ?? 0,
      [SideNavElementId.Hold]:
        masterAuthStatus?.[MasterAuthStatusValue.Hold] ?? 0,
      ...reviewPendingCounts,
      [SideNavElementId.QuickReview]: quickReviewCountState?.total_count ?? 0,
    };
  }, [
    postgresFacetsCount,
    reviewPendingCounts,
    quickReviewCountState?.total_count,
  ]);

  const loading = postgresLoading || reviewPendingLoading || quickReviewLoading;

  return { counts, loading };
};
